import "./ButtonInstance.css";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  documentPrompts,
  sampleSelectedTextActions,
  defaultAssistantsPrompts,
} from "../helpers/prompts";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules"; // Import Navigation module
import "swiper/swiper-bundle.css";
import SuggestedPrompts from "./SuggestedPrompts.js";
import { IconButton, Tooltip } from "@mui/material";
import { AttachmentContainer } from "./AttachmentContainer.js";
import Utils from "../utils/utils.js";
import GraphicEqIcon from "@mui/icons-material/GraphicEq";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ChatAssistantDropDown2 from "../pages/ChatAssistantDropdown2";

const ButtonInstance = (props) => {
  const [textareaValue, setTextareaValue] = useState("");
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const [selectedType, setSelectedType] = useState(
    "What do you want to do with this text?"
  );
  const [linesCount, setLinesCount] = useState(0);
  const [viewTitle, setViewTitle] = useState("Selected text");
  const [maxCharsPerLine, setMaxCharsPerLine] = useState(30);
  const [selectedTextActions, setSelectedTextActions] = useState(
    sampleSelectedTextActions
  );
  const [showAssistantDropdown, setShowAssistantDropdown] = useState(false);
  const assistantDropdownRef = useRef(null);
  const buttonRef = useRef(null);
  
  // Animated placeholder state
  const [currentPlaceholderIndex, setCurrentPlaceholderIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Portal state for dropdown positioning
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // New state for dynamic suggested prompts
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Portal state for guideline dropdown positioning
  const [guidelineDropdownPosition, setGuidelineDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [isPositionCalculated, setIsPositionCalculated] = useState(false);
  const guidelineDropdownRef = useRef(null);

  useEffect(() => {
  }, [showAssistantDropdown]);
  
  // Placeholder array
  let placeholders = [
    "Ask me anything...", // Important
    "Process loans in minutes!",
    "Calculate any type of income",
    "Attach any document to review",
    "Search guidelines",
    "Get a live rate quote",
    "Summarize AUS findings",
    "Ask me anything...",
    "Ready when you are!"
  ];

  if (props.overlayMode) {
    placeholders = [
      "Ask me anything...", // Important
      "How many loans are in my pipeline?",
      "Calculate any type of income",
      "Attach any document to review",
      "Search guidelines",
      "What's the minimum credit score for Fannie Mae?",
      "Get a live rate quote",
      "How many loans have lock expirations?",
      "What are today's rates?",
      "Get a loan estimate",
      "Analyze my loan pipeline",
      "How can we close more loans?",
      "Beat a loan estimate",
      "Ask me anything...",
      "Ready when you are!",
    ]
  }

  // Static action buttons for dynamic suggested prompts
  const staticActionButtons = [
    {
      id: 'guideline',
      label: 'Ask a guideline question',
      hasDropdown: true,
      svgIconPath: "/suggested_ask_question.svg",
      prompts: [
        "What's the minimum credit score for Fannie Mae?",
        "What are the DTI requirements for conventional loans?",
        "How do I calculate self-employed income?",
        "What documents are needed for a refinance?",
        "What are today's rates?",
        "What's the maximum loan amount for conventional?",
        "How do I verify employment for a loan?",
        "What are the seasoning requirements for cash-out?"
      ]
    },
    {
      id: 'price',
      label: 'Price a loan',
      hasDropdown: true,
      svgIconPath: "/suggested_price_a_loan.svg",
      prompts: [
        "Price a loan for a one-unit detached single family home in my area",
        "What's the current rate for a 30 year fixed loan in my area?",
        "What are the current market rates",
        "Rate for a $500k property in my area with a 680 credit score borrower",
      ]
    },
    {
      id: 'income',
      label: 'Calculate income',
      hasDropdown: true,
      svgIconPath: "/suggested_calculate_income.svg",
      prompts: [
        "Calculate Self-Employed income per Fannie Mae guidelines",
        "Calculate W-2 income per Fannie Mae guidelines",
        "Calculate W-2 income per Freddie Mac guidelines",
        "Calculate Self-Employed income per Freddie Mac guidelines",
        "I have a borrower $500 monthly social security income, calculate their qualified income?",
        "My borrower has a $1000 monthly alimony income, calculate their qualified income?",
      ]
    },
    {
      id: 'pipeline',
      label: 'Pipeline review',
      hasDropdown: true,
      svgIconPath: "/suggested_pipeline_review.svg",
      prompts: [
        "How many loans are in my pipeline?",
        "What's the total amount of loans in my pipeline?",
        "How many loans have lock expirations soon?",
        "Find me a loan for a specific borrower",
        "Which documents are available for a specific borrower?",
      ]
    },
    {
      id: 'beat',
      label: 'Beat a loan estimate',
      hasDropdown: true,
      svgIconPath: "/suggested_beat_loan_estimate.svg",
      prompts: [
        "Beat this loan estimate with a better rate",
        "Beat this loan estimate with a better monthly payment",
        "Give me options to beat this loan estimate",
      ]
    }
  ];

  const urlParams = new URLSearchParams(window.location.search);
  const clientHostname = urlParams.get("clientHostname");

  const inputRef = useRef(null);
  const clientID = props.clientID;
  const data = props.data;
  const currentlyTyping = props.currentlyTyping;
  const numberOfMessagesInChat = props.numberOfMessagesInChat;
  const currentView = props.currentView;
  const uid = props.uid;
  const isDocumentChat = props.isDocumentChat;
  const compareMode = props.compareMode;
  const overlayMode = props.overlayMode;

  let titleMessage;
  let descriptionMessage;
  if (clientHostname && !clientHostname.includes("addy")) {
    // If the client hostname exists (meaning this is embeded in another) website
    // for instance chrome extension and hostname is not addy
    titleMessage = "How can I help";
    descriptionMessage = "";
    if (
      clientHostname === "app.heynouri.ai" ||
      clientHostname === "nourimortgagedev.addy.so"
    ) {
      titleMessage = "How can I help you today?"; // Was previously search lending guidelines
      descriptionMessage =
        "I can answer questions about mortgage lending guidelines, loan programs, and more";
    }
  } else {
    titleMessage =
      props.selectedAssistant?.appID ===
      "addy-default-assistant-c66e0609-371e-49b2-bb4a-5186526b2a9d"
        ? "How can I help you today?" // Was previously search lending guidelines
        : "How can I help you today?";
    descriptionMessage =
      props.selectedAssistant?.appID ===
      "addy-default-assistant-c66e0609-371e-49b2-bb4a-5186526b2a9d"
        ? "I can answer questions about mortgage lending guidelines, loan programs, and more"
        : `I can answer questions from the ${
            props.selectedAssistant?.appName || ""
          } assistant training data`;
  }

  const [selectedText, setSelectedText] = useState(
    data?.action === "selectedText" && data?.text ? data.text : ""
  );

  // eslint-disable-next-line no-undef
  const chatInfo = document.querySelector("#addy-chat-info");
  if (selectedText && selectedText.length && chatInfo) {
    chatInfo.remove();
  }

  // When the component loads for the first time, once, make the cursor
  // focus on the textarea
  useEffect(() => {
    const textarea = document.querySelector(".addy-prompt-input");
    if (textarea) {
      textarea.focus();
    }
  }, []);

  useEffect(() => {
    // console.log("props.selectedAssistant", props.selectedAssistant);
    if (
      props.selectedAssistant &&
      props?.selectedAssistant?.suggestedQuestions
    ) {
      // select random 3 if there are more than 3
      const randomPrompts2 = props?.selectedAssistant?.suggestedQuestions
        .sort(() => 0.5 - Math.random())
        .slice(0, 3);
      setSuggestedPrompts(randomPrompts2);
    } else {
      const randomPrompts2 = defaultAssistantsPrompts[
        props?.selectedAssistant?.defaultId || "addy-assistant-001"
      ].sort(() => 0.5 - Math.random());
      setSuggestedPrompts(randomPrompts2.slice(0, 3));
    }
  }, [props.selectedAssistant]);

  const setButtonInstancePosition = (numMessages, currentView) => {
    let alignInputBottom = overlayMode || props?.alignInputBottom;
    if (overlayMode) alignInputBottom = false; // Align input to top right now
    const buttonInstance = document.querySelector(".button-instance");
    if (!buttonInstance) return;
    if (
      numMessages === 0 &&
      currentView === "select-assistant" &&
      !selectedText
    ) {
      // Set the postion to the center of the page
      buttonInstance.style.bottom = alignInputBottom ? "20px" : "40%";
    } else {
      buttonInstance.style.bottom = "20px";
    }
  };

  const toggleCompareMode = async (compareModeT) => {
    const newCompareMode = !compareModeT;
    await props.onCompareModeChange(newCompareMode);
  };

  // When number of messages in chat changes, set button instance margin bottom
  useEffect(() => {
    setButtonInstancePosition(numberOfMessagesInChat, currentView);
  }, [numberOfMessagesInChat, currentView, selectedText]);

  // Animated placeholder logic
  useEffect(() => {
    let shouldAnimate = props.animatedPlaceholders !== undefined 
      ? props.animatedPlaceholders 
      : !props.overlayMode;

    if (props.numberOfMessagesInChat > 0) {
      shouldAnimate = false;
      // set placeholder index to 0
      setCurrentPlaceholderIndex(0);
    }

    if (!shouldAnimate) return;

    const interval = setInterval(() => {
      setIsAnimating(true);
      
      setTimeout(() => {
        setCurrentPlaceholderIndex((prevIndex) => {
          const newIndex = (prevIndex + 1) % placeholders.length;
          return newIndex;
        });
        setIsAnimating(false);
      }, 150); // Half of transition time for fade out
      
    }, 4000); // Change every 3 seconds

    return () => clearInterval(interval);
  }, [props.animatedPlaceholders, props.overlayMode, props.numberOfMessagesInChat]);

  useEffect(() => {
    if (clientID && uid) {
      // Whenever a user selects text on the page, listen for it
      listenForSelectedFromClientWebpage(clientID);
    }
    // Set suggested prompts to be 10 random prompts from samplePrompts
    if (props?.selectedAssistant) {
      if (props?.selectedAssistant?.suggestedQuestions) {
        // select random 3 if there are more than 3
        const randomPrompts2 = props?.selectedAssistant?.suggestedQuestions
          .sort(() => 0.5 - Math.random())
          .slice(0, 3);
        setSuggestedPrompts(randomPrompts2);
      } else {
        const randomPrompts2 = defaultAssistantsPrompts[
          props?.selectedAssistant?.defaultId || "addy-assistant-001"
        ].sort(() => 0.5 - Math.random());
        setSuggestedPrompts(randomPrompts2.slice(0, 3));
      }
    } else {
      const randomPrompts2 = defaultAssistantsPrompts[
        props?.selectedAssistant?.defaultId || "addy-assistant-001"
      ].sort(() => 0.5 - Math.random());
      setSuggestedPrompts(randomPrompts2.slice(0, 3));
    }

    if (data && data["action"] === "selectedText") {
      setSelectedText(data["text"]);
    }
  }, [clientID]); // Run this effect when clientID changes

  useEffect(() => {
    // remove chat info onboarding text if selected text is present
    if (selectedText && selectedText.length && chatInfo) {
      chatInfo.remove();
    }
  }, [selectedText]); // Reload component on selected text change

  // Reload component on currentlyTyping change
  useEffect(() => {}, [currentlyTyping]);

  // Reload when props change
  useEffect(() => {
    if (
      props.currentEmailAction === "Analyze attachment" &&
      props.selectedAttachment
    ) {
      setSelectedText(props.selectedAttachment.name || "");
      setSelectedType("More ideas for you");
      setViewTitle("Chat with your document");
      if (
        props.selectedAttachment.tag &&
        documentPrompts[props.selectedAttachment.tag]?.length
      ) {
        setSelectedTextActions(documentPrompts[props.selectedAttachment.tag]);
      } else {
        setSelectedTextActions([
          "Extract text",
          "Translate",
          "Write a blog post about this document",
        ]);
      }
      // get the analyze attachment parent indicator
      // const analyzeAttachmentParent = document.querySelector(".selected-text-parent");
      // if (analyzeAttachmentParent) {
      //     analyzeAttachmentParent.classList.add("analyze-attachment-parent");
      // }
    } else if (props.currentEmailAction === "Analyze document") {
      setSelectedText(props.selectedDocument?.name || "My document.pdf");
      setSelectedType("More ideas for you");
      setViewTitle("Chat with your document");
      if (
        props.selectedDocument?.tag &&
        documentPrompts[props.selectedDocument?.tag]?.length
      ) {
        setSelectedTextActions(documentPrompts[props.selectedDocument.tag]);
      } else {
        setSelectedTextActions([
          "Extract text",
          "Translate",
          "Write a blog post about this document",
        ]);
      }
    } else if (
      props.currentEmailAction === "Other files" &&
      props.selectedAttachment
    ) {
      setSelectedText(props.selectedAttachment.name || "");
      setSelectedType("More ideas for you");
      setViewTitle("Chat with your file");
    } else {
      if (selectedText) {
        setSelectedType("What do you want to do with this text?");
        setViewTitle("Selected text");
        setSelectedTextActions(sampleSelectedTextActions);
      }
    }
    if (props.maxCharsPerLine) {
      setMaxCharsPerLine(props.maxCharsPerLine);
    }
  }, [props]);

  // Handle clicks outside dropdown
  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener('click', handleClickOutsideDropdown);
      return () => document.removeEventListener('click', handleClickOutsideDropdown);
    }
  }, [isDropdownOpen]);

  // Reload when linesCount changes
  useEffect(() => {}, [linesCount]);

  // Handle click outside for assistant dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        assistantDropdownRef.current &&
        !assistantDropdownRef.current.contains(event.target)
      ) {
        console.log("handleClickOutside setShowAssistantDropdown to false");
        setShowAssistantDropdown(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  // Handle change event of textarea
  const handleChange = (event) => {
    setTextareaValue(event.target.value);
    const textareaWidth = event.target.getBoundingClientRect().width;
    const lineBreakCount = event.target.value.split(/\r\n|\r|\n/).length;
    const lengthBasedCount = calculateLineCount(textareaWidth);
    const count =
      event.target.value === ""
        ? 1
        : Math.max(lineBreakCount, lengthBasedCount);
    setLinesCount(count <= 5 ? count : 5);
  };

  const calculateLineCount = (elementWidth) => {
    const charWidth = 7;
    const maxCharactersPerLine = Math.floor(elementWidth / charWidth);
    return Math.ceil(textareaValue.length / maxCharactersPerLine);
  };

  // Handle key down event of textarea
  const handleKeyDown = (event) => {
    if (event.key === "Enter" && (event.shiftKey || event.altKey)) {
      // If Shift+Enter or Alt+Enter is pressed, create a new line
      event.stopPropagation();
      if (linesCount < 5) {
        setTextareaValue((prevValue) => prevValue + "\n");
      }
    } else if (event.key === "Enter") {
      onClick(textareaValue, selectedText, compareMode);
      event.preventDefault();
    }
  };

  const onClick = () => {
    if (!textareaValue || currentlyTyping) return;
    props.onClick(
      textareaValue,
      selectedText,
      compareMode,
      compareMode ? "true" : "false",
      props.files
    );
    setTextareaValue("");
    setLinesCount(1);
    // Reset the selected text
    setSelectedText("");
  };

  const onSuggestedPromptClick = (prompt) => {
    if (currentlyTyping) return;
    // Reset the selected text
    props.onClick(
      prompt,
      selectedText,
      compareMode,
      compareMode ? "true" : "false"
    );
    setSelectedText("");
  };
  const onSelectedTextClose = async () => {
    if (isDocumentChat) {
      await props.onCloseAttachment();
    }
    setSelectedText("");
  };

  const closeHotTip = () => {
    const hotTipDiv = document.getElementById("hot-tip-div");
  };

  const suggestedPromptsCarousel = (prompts) => {
    return (
      <SuggestedPrompts
        prompts={prompts}
        onClick={(prompt) => onSuggestedPromptClick(prompt)}
      />
    );
  };

  const listenForSelectedFromClientWebpage = async (id) => {
    // eslint-disable-next-line no-undef
    window.addEventListener("message", (event) => {
      const data = event.data;
      if (data && data.key) {
        const action = data.key;
        const text = data.value;
        switch (action) {
          case "selectedText":
            setSelectedText(text);
            break;
          default:
            break;
        }
      }
    });
  };

  const handleFileChange = async (e) => {
    const acceptedFiles = Array.from(e.target.files);

    acceptedFiles.forEach((file) => {
      const id = `${file.name}-${Date.now()}`;
      const preview = URL.createObjectURL(file);

      // Immediately add to state as "loading"
      const tempFile = {
        id,
        name: file.name,
        type: file.type,
        preview,
        loading: true,
      };
      props.setFiles((prevFiles) => [...prevFiles, tempFile]);

      const reader = new FileReader();

      reader.onloadend = async () => {
        const base64 = reader.result;

        // Check for PDF header (25504446)
        const arr = new Uint8Array(
          atob(base64.split(",")[1])
            .split("")
            .map((char) => char.charCodeAt(0))
        ).subarray(0, 4);

        let header = "";
        for (let i = 0; i < arr.length; i++) {
          header += arr[i].toString(16);
        }

        if (header === "25504446") {
          file = new File([file], file.name, { type: "application/pdf" });
        }

        // Now do backend call
        const response = await Utils.base64ToText(
          base64,
          file.type.split("/")[1]
        );

        if (response.success) {
          if (response.textContent?.length === 0) {
            props.toastify("There was no text found in the file.", false, true);
          }
          props.setFiles((prevFiles) =>
            prevFiles.map((f) =>
              f.id === id
                ? {
                    ...f,
                    loading: false,
                    base64,
                    textContent: response.textContent,
                  }
                : f
            )
          );
        } else {
          // Remove file from UI on failure
          props.setFiles((prevFiles) => prevFiles.filter((f) => f.id !== id));
          props.toastify(
            "Error while processing file, please try another file.",
            false
          );
        }
      };

      reader.readAsDataURL(file);
    });
  };

  const handleFiles = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const onAssistantClick = (assistant) => {
    // Call the parent's assistant selection handler
    if (props.onAssistantClick) {
      props.onAssistantClick(assistant);
    }
    console.log("onAssistantClick setShowAssistantDropdown to false");
    setShowAssistantDropdown(false);
  };

  const updateDefaultAssistant = (assistant, e) => {
    // Call the parent's default assistant update handler
    if (props.updateDefaultAssistant) {
      props.updateDefaultAssistant(assistant, e);
    }
  };

  // Calculate dropdown position for portal
  const calculateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4, // 4px margin
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  };

  // Calculate guideline dropdown position for portal
  const calculateGuidelineDropdownPosition = (buttonElement) => {
    // Target the button-instance-inner element
    const buttonInstanceInner = document.querySelector('.button-instance-inner');
    
    console.log('Button instance inner found:', buttonInstanceInner);
    
    if (buttonInstanceInner) {
      const rect = buttonInstanceInner.getBoundingClientRect();
      console.log('Button instance inner rect:', rect);
      console.log('Window scroll:', window.scrollY, window.scrollX);
      
      const position = {
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width
      };
      
      console.log('Calculated position:', position);
      setGuidelineDropdownPosition(position);
      setIsPositionCalculated(true);
    } else if (buttonElement) {
      // Fallback: use button position and estimate position
      const rect = buttonElement.getBoundingClientRect();
      console.log('Using button fallback, rect:', rect);
      
      const position = {
        top: rect.bottom + window.scrollY + 4, // Below the button
        left: rect.left + window.scrollX,
        width: Math.max(rect.width * 3, 400) // Estimate width
      };
      
      console.log('Fallback position:', position);
      setGuidelineDropdownPosition(position);
      setIsPositionCalculated(true);
    } else {
      console.log('No button-instance-inner or button element found');
    }
  };

  // Static button styles
  const staticButtonsStyles = {
    container: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      justifyContent: 'center',
      marginTop: '10px',
      paddingBottom: '15px',
    },
    button: {
      padding: '8px 16px',
      borderRadius: '20px',
      border: '1px solid rgb(213, 214, 219, 0.6)',
      backgroundColor: 'white',
      color: '#485165',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    activeButton: {
    //   backgroundColor: '#6e1bfa',
    //   color: 'white',
    //   borderColor: '#6e1bfa'
    },
    dropdownArrow: {
      fontSize: '12px',
      transition: 'transform 0.2s ease'
    },
    openArrow: {
      transform: 'rotate(180deg)'
    },
    dropdownMenu: {
      position: 'absolute',
      top: '100%',
      left: '0',
      width: '100%', // Use full width of parent
      backgroundColor: 'white',
      border: '1px solid rgb(213, 214, 219, 0.3)',
      borderRadius: '25px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      zIndex: 1000,
      maxHeight: '200px',
      overflowY: 'auto',
      marginTop: '4px',
      fontFamily: 'Inter, sans-serif'
    },
    dropdownItem: {
      padding: '12px 16px',
      cursor: 'pointer',
    //   borderBottom: '1px solid #f0f0f0',
      fontSize: '13px',
      fontFamily: 'Inter, sans-serif',
      transition: 'background-color 0.2s ease'
    }
  };

  // Static button click handlers
  const handleStaticButtonClick = (button, buttonElement) => {
    if (button.hasDropdown) {
      if (!isDropdownOpen) {
        // Add small delay to ensure DOM is ready
        setTimeout(() => {
          calculateGuidelineDropdownPosition(buttonElement);
        }, 10);
      }
      setIsDropdownOpen(!isDropdownOpen);
      setSelectedCategory(button.id);
    } else {
      // Handle direct action buttons
      onSuggestedPromptClick(button.label);
    }
  };

  const handleDropdownItemClick = (prompt) => {
    onSuggestedPromptClick(prompt);
    setIsDropdownOpen(false);
    setSelectedCategory(null);
  };

  const handleClickOutsideDropdown = (event) => {
    if (isDropdownOpen && !event.target.closest('.static-buttons-container') && !guidelineDropdownRef.current?.contains(event.target)) {
      setIsDropdownOpen(false);
      setSelectedCategory(null);
      setIsPositionCalculated(false);
    }
  };

  return (
    <div
      className={`button-instance ${
        props.isHomePage ? "" : "button-instance-padding"
      } ${props.overlayMode ? "ai-gradient-border-2" : ""}`}
      style={
        props.overlayMode
          ? {
            //   paddingTop: "3px",
              background: "white",
              borderRadius: "15px",
              padding: "4px",
            }
          : {}
      }
    >
      <div className="" style={{
        // border: "1px solid rgba(0, 0, 0, 0.6)",
        background: "white",
        width: "100%",
        borderRadius: "15px",
      }}>
      {props.hideHotTipContainer &&
        !props.hideSuggestedPrompts &&
        !selectedText &&
        !props.overlayMode && (
          <div className="lightning-instance-parent">
            {
              <div
                className="more-suggestions-title gradient-title"
                style={{
                  textAlign: "center",
                  paddingLeft: "5px",
                  fontSize: "26px",
                  // letterSpacing: "0px",
                  fontWeight: "bold",
                  color: "#485165",
                  // paddingBottom: "20px",
                  color: "rgba(0, 0, 0, 0.9)",
                  color: "#a179e4",
                  paddingBottom: "10px",
                  // color: "#745dde",
                }}
              >
                {titleMessage}
              </div>
            }

            {/* {
              <p
                className="add-necessary-bits small-description-text description-helper-text"
                style={{
                  color: "#485165",
                  width: "100%",
                  // paddingBottom: "15px",
                }}
              >
                {descriptionMessage}
              </p>
            } */}
          </div>
        )}

      {/* Assistant Selection Dropdown - ONLY in Overlay Mode */}
      {props.overlayMode && (
        <div className="assistant-selection-container" style={{ marginBottom: "10px", marginLeft: "8px", marginTop: "5px", width: "fit-content", position: "relative"}}>
          <div
            ref={buttonRef}
            className="assistant-selector-button"
            onClick={(e) => {
              e.stopPropagation();
              if (!showAssistantDropdown) {
                calculateDropdownPosition();
              }
              setShowAssistantDropdown(!showAssistantDropdown);
            }}
            style={{
              display: "flex",
              alignItems: "center",
              marginLeft: "5px",
              justifyContent: "space-between",
              padding: "5px 8px",
            //   border: "1px solid #D5D6DB",
              borderRadius: "8px",
              backgroundColor: "white",
              cursor: "pointer",
              fontSize: "13px",
              color: "#485165",
              width: "fit-content",
              maxWidth: "300px",
            //   boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {/* {props.selectedAssistant?.imageUrl ? (
                <img
                  src={props.selectedAssistant.imageUrl}
                  alt=""
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    // borderRadius: "50%",
                    // backgroundColor: "#F0F1F6",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                  }}
                >
                  <img
                  src={"https://i.imgur.com/BbyEMeR.jpeg"}
                  alt=""
                  style={{
                    width: "20px",
                    height: "20px",
                    // borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
                </div>
              )} */}
              <span style={{ paddingRight: "10px" }}>
                {props.selectedAssistant?.appName || "Select Agent"}
              </span>
            </div>
            <img
              src="/expand.svg"
              alt=""
              style={{
                width: "12px",
                height: "12px",
                transform: showAssistantDropdown ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s ease",
              }}
            />
          </div>

          {/* Dropdown Content - Now handled by Portal */}
        </div>
      )}

      <div
        className={`button-instance-container ${
          props.overlayMode ? "" : ""
        }`}
        style={
          overlayMode
            ? {
                background: "white",
                borderRadius: "15px",
                border: "2px solid transparent",
              }
            : {}
        }
      >
        {selectedText?.trim() && numberOfMessagesInChat === 0 ? (
          <div className="selected-text-container">
            <div
              className="selected-text-parent"
              style={{
                background: "#ffffff",
                zIndex: 99,
              }}
            >
              <div className="selected-text-header-parent">
                <div className="selected-text-header">{viewTitle}</div>

                <div
                  className="selected-text-close-button"
                  onClick={() => onSelectedTextClose()}
                >
                  <img width="12" height="12" src="/close.svg" />
                </div>
              </div>
              <div className="selected-text">
                {selectedText.length > 250
                  ? selectedText.substring(0, 250) + "..."
                  : selectedText}
              </div>
            </div>

            <div className="actions-container">
              <div className="action-title">{selectedType}</div>

              <div className="selected-text-action-div">
                {selectedTextActions.map((action, index) => (
                  <div
                    onClick={() => onSuggestedPromptClick(action)}
                    key={index}
                    className="suggestion-button selected-text-action"
                  >
                    {action}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : numberOfMessagesInChat === 0 ? (
          !props.hideHotTipContainer && (
            <div className="hot-tip-parent-container">
              <div
                className="selected-text-parent hot-tip-parent"
                id="hot-tip-div"
              >
                <div className="selected-text-header-parent">
                  <div className="selected-text-header">Hot tip!</div>

                  {/* <div className="selected-text-close-button" onClick={() => closeHotTip()}>
                                    <img width="12" height="12" src="/close.svg" />
                                </div> */}
                </div>
                <div className="selected-text">
                  Press and hold the{" "}
                  <mark className="focus-text-mark">`Control` </mark>
                  key, then highlight text on any website to quickly open Addy
                  AI. Try it now!
                </div>
              </div>

              {!props.hideSuggestedPrompts && (
                <div className="lightning-instance-parent">
                  {props.showMoreSuggestionsForYouTitle && (
                    <div className="more-suggestions-title">
                      More suggestions for you
                    </div>
                  )}
                  <div className="lightning-instance tooltip-5">
                    {suggestedPrompts.map((prompt, index) => (
                      <div
                        onClick={() => onSuggestedPromptClick(prompt)}
                        key={index}
                        className="suggestion-button"
                      >
                        {prompt}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )
        ) : (
          !props.hideSuggestedPrompts && (
            <div className="lightning-instance-parent">
              {props.showMoreSuggestionsForYouTitle && (
                <div className="more-suggestions-title">
                  More suggestions for you
                </div>
              )}
              <div className="lightning-instance tooltip-5" style={{
                width: "100%",
              }}>
                {suggestedPrompts.map((prompt, index) => (
                  <div
                    onClick={() => onSuggestedPromptClick(prompt)}
                    key={index}
                    className="suggestion-button"
                  >
                    {prompt}
                  </div>
                ))}
              </div>
            </div>
          )
        )}

        <div
          className="button-instance-inner tooltip-6"
          style={
            overlayMode
              ? {
                  background: "white",
                  boxShadow: "none",
                }
              : {}
          }
        >
          <div
            className="lightning-parent"
            style={{
              display: "flex",
              flexDirection: props.overlayMode ? "row" : "column",
              alignItems: "start",
              justifyContent: "start",
            }}
          >
            {props?.files?.length > 0 && (
              <div
                className={
                  "p-2 pb-[56px] w-[100%] align-start justify-start flex flex-col"
                }
              >
                <AttachmentContainer
                  files={props.files}
                  setFiles={props.setFiles}
                />
              </div>
            )}
            <textarea
              className={`addy-prompt-input ${isAnimating ? 'animating' : ''}`}
              style={{
                height: `${linesCount}em`,
                maxWidth: props.overlayMode ? "80%" : "100%",
                height: props.overlayMode ? "100%" : "1em",
              }}
              placeholder={
                placeholders[currentPlaceholderIndex] || props.placeholder
              }
              value={textareaValue}
              onKeyDown={handleKeyDown}
              onChange={handleChange}
            />

            <div
              className="input-actions-div flex justify-between items-center w-full"
              style={
                overlayMode
                  ? {
                      width: "unset",
                    }
                  : {}
              }
            >
              <div className="input-actions-div-2 flex justify-between items-center w-full gap-2">
                {props.overlayMode && (
                  <Tooltip title="More options" arrow>
                    <div>
                      <IconButton
                        onClick={() => setShowMoreOptions(!showMoreOptions)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </div>
                  </Tooltip>
                )}
                <div
                  onClick={() => setShowMoreOptions(!showMoreOptions)}
                  style={
                    overlayMode
                      ? {
                          display: showMoreOptions ? "flex" : "none",
                          background: "#FFFFFF",
                          borderRadius: "10px",
                          padding: "10px",
                          width: "fit-content",
                          height: "fit-content",
                          position: "absolute",
                          top: "5px",
                          right: "165px",
                          zIndex: "999999",
                          boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
                          paddingTop: "5px",
                          paddingBottom: "5px",
                        }
                      : {
                          display: "flex",
                          flexDirection: "row",
                        }
                  }
                >
                  <div
                    className="flex items-center gap-2 justify-start items-start"
                    style={{ marginRight: "9px" }}
                  >
                    <Tooltip title="Attach file" arrow>
                      <div
                        onClick={handleFiles}
                        className="input-action-button inline-flex items-center gap-1"
                        style={{
                          color: "#6E717C",
                          border: "1px solid #D5D6DB",
                          backgroundColor: "transparent",
                        }}
                      >
                        <img
                          data-toggle="tooltip"
                          title="Attach file"
                          className="button-icon-2"
                          loading="lazy"
                          alt=""
                          // Change the source based on current value of textarea

                          src="/attachFile.svg"
                        />
                      </div>
                    </Tooltip>
                  </div>
                  {/* Chip for comparison */}
                  {!!props.onCompareModeChange && (
                    <Tooltip
                      key={"compare-mode-button"}
                      title={"Compare investor guidelines"}
                      arrow
                    >
                      <div
                        onClick={() => toggleCompareMode(compareMode)}
                        className="input-action-button inline-flex items-center gap-1"
                        style={
                          compareMode
                            ? {
                                color: "#8740FF",
                                border: "1px solid #D5C9EE",
                                backgroundColor: "#DBD3EE",
                              }
                            : {
                                color: "#6E717C",
                                border: "1px solid #D5D6DB",
                                backgroundColor: "transparent",
                              }
                        }
                      >
                        <img
                          src={
                            compareMode
                              ? "/compare-logo-purple.svg"
                              : "/compare-logo-grey.svg"
                          }
                          width="18"
                          height="18"
                        />
                        Compare
                      </div>
                    </Tooltip>
                  )}
                </div>
              </div>
              <div className="input-actions-div-2 flex justify-between items-center w-full gap-2">
                <Tooltip
                  key={"compare-mode-button"}
                  title={"Send message"}
                  arrow
                >
                  <div
                    className="flex items-center gap-2 justify-start items-start"
                    style={{ marginRight: "9px" }}
                  >
                    <div
                      onClick={() =>
                        onClick(textareaValue, selectedText, compareMode)
                      }
                      className="input-action-button-2 inline-flex items-center gap-1"
                      style={{
                        color:
                          props.selectedAssistant &&
                          textareaValue &&
                          !currentlyTyping &&
                          props?.files?.every((file) => !file?.loading)
                            ? "#FFFFFF"
                            : "#6E717C",
                        border: "1px solid #D5D6DB",
                        backgroundColor:
                          props.selectedAssistant &&
                          textareaValue &&
                          !currentlyTyping &&
                          props?.files?.every((file) => !file?.loading)
                            ? "#8740FF"
                            : "lightgrey",
                        cursor:
                          props.selectedAssistant &&
                          textareaValue &&
                          !currentlyTyping &&
                          props?.files?.every((file) => !file?.loading)
                            ? "pointer"
                            : "not-allowed",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (
                          props.selectedAssistant &&
                          textareaValue &&
                          !currentlyTyping &&
                          props?.files?.every((file) => !file?.loading)
                        ) {
                          e.currentTarget.style.backgroundColor = "#6e1bfa"; // Purple color on hover
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (
                          props.selectedAssistant &&
                          textareaValue &&
                          !currentlyTyping &&
                          props?.files?.every((file) => !file?.loading)
                        ) {
                          e.currentTarget.style.backgroundColor = "#8740FF"; // Original color when not hovering
                        }
                      }}
                    >
                      <ArrowUpwardIcon />
                    </div>
                  </div>
                </Tooltip>

                <Tooltip
                  key={"voice-mode-button"}
                  title={"Enter voice mode"}
                  arrow
                >
                  <div
                    onClick={() => {
                      if (props.recording) {
                        props.stopRecording();
                      } else {
                        props.startRecording();
                      }
                    }}
                    className="input-action-button inline-flex items-center gap-1"
                    style={{
                      color: "#6E717C",
                      border: "1px solid #D5D6DB",
                      backgroundColor: "#transparent",
                    }}
                  >
                    <GraphicEqIcon />
                  </div>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
        {numberOfMessagesInChat === 0 && !props.hideSuggestedPrompts && (
          <div className="static-buttons-container" style={staticButtonsStyles.container}>
            {staticActionButtons.map((button) => (
              <div key={button.id} style={{ position: 'relative' }}>
                <button
                  style={{
                    ...staticButtonsStyles.button,
                    ...(selectedCategory === button.id && isDropdownOpen ? staticButtonsStyles.activeButton : {})
                  }}
                  onClick={(e) => {
                    const buttonElement = e.currentTarget;
                    handleStaticButtonClick(button, buttonElement);
                  }}
                >
                  <img src={button.svgIconPath} alt="" style={{ width: '16px', height: '16px', }} />
                  {button.label}
                  {button.hasDropdown && ( null
                    // <span style={{
                    //   ...staticButtonsStyles.dropdownArrow,
                    //   ...(isDropdownOpen ? staticButtonsStyles.openArrow : {})
                    // }}>
                    //   ▼
                    // </span>
                  )}
                </button>
              </div>
            ))}
          </div>
        )}

        {!props.hideFeedbackRequestText && !props.overlayMode && (
          <div className="selected-text feedback-request-text">
            <a href="https://forms.gle/z5mJsnJKNGiX7SGt8" target="_blank">
              Give us feedback
            </a>{" "}
            to help improve Addy AI
          </div>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/png,image/jpeg,image/jpg,application/pdf"
        style={{ display: "none" }}
        max={100}
        onChange={(e) => {
          // max 5 files in used and files
          const totalFiles =
            props.files?.length +
            e.target.files.length +
            props.usedFiles?.length;
          if (totalFiles > 5) {
            props.toastify(
              "You can only upload up to 5 files per chat.",
              false
            );
            return;
          }
          handleFileChange(e);
        }}
      />
      </div>
      
      {/* Portal for Assistant Dropdown */}
      {showAssistantDropdown && createPortal(
        <div
          ref={assistantDropdownRef}
          style={{
            position: "absolute",
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`,
            zIndex: 9999,
          }}
        >
          <ChatAssistantDropDown2
            overlayMode={props.overlayMode}
            numberOfMessagesInChat={props.numberOfMessagesInChat}
            displayPosition="bottom"
            assistants={props.assistants || []}
            hideAvatar={false}
            selectedAssistant={props.selectedAssistant}
            referencedElementId="assistant-selector-button"
            defaultAssistant={props.defaultAssistant}
            onAssistantClick={onAssistantClick}
            updateDefaultAssistant={updateDefaultAssistant}
            headerText="Agents"
            createNewLink="https://app.addy-ai.com"
            hideTabs={true} // Simplified view for ButtonInstance
            smallContainer={true} // Compact mode
          />
        </div>,
        document.body
      )}

      {/* Portal for Guideline Dropdown */}
      {isDropdownOpen && selectedCategory && isPositionCalculated && createPortal(
        <div
          ref={guidelineDropdownRef}
          style={{
            position: "absolute",
            top: `${guidelineDropdownPosition.top}px`,
            left: `${guidelineDropdownPosition.left}px`,
            width: `${guidelineDropdownPosition.width}px`,
            zIndex: 9999,
          }}
        >
          <div style={staticButtonsStyles.dropdownMenu}>
            {staticActionButtons.find(btn => btn.id === selectedCategory)?.prompts.map((prompt, index) => (
              <div
                key={index}
                style={staticButtonsStyles.dropdownItem}
                onClick={() => handleDropdownItemClick(prompt)}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
              >
                {prompt}
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default ButtonInstance;
